import React, { useState, useEffect } from 'react';
import { useNotePriorities } from '../../../../hooks/useNotePriorities';
import { useAlert } from '../../../../hooks';
import { FaPlus } from 'react-icons/fa';
import NotePriorityForm from './NotePriorityForm';
import NotePriorityList from './NotePriorityList';
import styles from './NotePriorities.module.css';

const NotePriorities = () => {
    const [showForm, setShowForm] = useState(false);
    const [editingPriority, setEditingPriority] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const { setInfoAlert, setErrorAlert, setIsModalOpen } = useAlert();

    const {
        priorities,
        loading,
        error,
        createPriority,
        updatePriority,
        deletePriority,
        checkNameExists,
        checkWeightExists,
        refetch
    } = useNotePriorities();

    // Track modal state for alert positioning and header blocking
    useEffect(() => {
        if (showForm) {
            document.body.setAttribute('data-modal-open', 'true');
            setIsModalOpen(true);
        } else {
            document.body.removeAttribute('data-modal-open');
            setIsModalOpen(false);
        }
        return () => {
            document.body.removeAttribute('data-modal-open');
            setIsModalOpen(false);
        };
    }, [showForm, setIsModalOpen]);

    // Handle create/edit form submission
    const handleFormSubmit = async (formData) => {
        // Store current scroll position
        const scrollPosition = window.scrollY;
        
        try {
            if (editingPriority) {
                await updatePriority(editingPriority.priorityId, formData);
                setInfoAlert('Note priority updated successfully');
            } else {
                await createPriority(formData);
                setInfoAlert('Note priority created successfully');
            }
            setShowForm(false);
            setEditingPriority(null);
            
            // Restore scroll position after state updates and re-render
            setTimeout(() => window.scrollTo(0, scrollPosition), 0);
        } catch (error) {
            setErrorAlert(error.message || 'Operation failed');
        }
    };

    // Handle edit
    const handleEdit = (priority) => {
        setEditingPriority(priority);
        setShowForm(true);
    };

    // Handle delete
    const handleDelete = async (priority) => {
        if (window.confirm(`Are you sure you want to delete "${priority.priorityName}"?`)) {
            // Store current scroll position
            const scrollPosition = window.scrollY;
            
            try {
                await deletePriority(priority.priorityId);
                setInfoAlert('Note priority deleted successfully');
                
                // Restore scroll position after state updates and re-render
                setTimeout(() => window.scrollTo(0, scrollPosition), 0);
            } catch (error) {
                setErrorAlert(error.message || 'Failed to delete note priority');
                // Trigger a refetch to clear the error state and reload the list
                // This ensures the UI returns to normal view after showing the error
                await refetch();
            }
        }
    };

    // Cancel form
    const handleCancel = () => {
        setShowForm(false);
        setEditingPriority(null);
    };

    // Filter priorities by search query
    const filteredPriorities = priorities.filter(priority => {
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            return priority.priorityName.toLowerCase().includes(query);
        }
        return true;
    });

    if (error) {
        return (
            <div className={styles.container}>
                <div className={styles.header}>
                    <h2>Note Priorities Management</h2>
                    <p>Manage note priority levels and their weights for sorting and prioritization.</p>
                </div>

                <div className={styles.error}>
                    <h3>Error Loading Note Priorities</h3>
                    <p>{error}</p>
                    <div className={styles.errorActions}>
                        <button onClick={refetch} className={styles.retryButton}>
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2>Note Priorities Management</h2>
                <p>Manage note priority levels and their weights for sorting and prioritization.</p>
            </div>

            {/* Info Box */}
            <div className={styles.infoBox}>
                <h4>Priority Weight Guidelines</h4>
                <ul>
                    <li><strong>Higher weight = Higher priority</strong></li>
                    <li>Suggested scale: Critical (100), High (75), Medium (50), Low (25), Minimal (10)</li>
                    <li>Multiple priorities can share the same weight</li>
                    <li>Priorities are displayed in descending order by weight (highest first)</li>
                </ul>
            </div>

            {/* Controls */}
            <div className={styles.controls}>
                <button
                    onClick={() => setShowForm(true)}
                    className={styles.addButton}
                    disabled={loading}
                >
                    <FaPlus className={styles.buttonIcon} /> Create New Priority
                </button>
            </div>

            {/* Form Modal */}
            {showForm && (
                <div className={styles.modal}>
                    <div className={styles.modalContent}>
                        <NotePriorityForm
                            priority={editingPriority}
                            onSubmit={handleFormSubmit}
                            onCancel={handleCancel}
                            loading={loading}
                            checkNameExists={checkNameExists}
                            checkWeightExists={checkWeightExists}
                        />
                    </div>
                </div>
            )}

            {/* Priorities List */}
            <NotePriorityList
                priorities={filteredPriorities}
                loading={loading}
                onEdit={handleEdit}
                onDelete={handleDelete}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
            />
        </div>
    );
};

export default NotePriorities;
